import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import {
  Button,
  Box,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  ButtonGroup,
} from '@mui/material';
import AddRemoveArtist from '../../components/Common/AddRemoveArtist';
import SetUp from '../../components/EnquiryProcessor/SetUp';
import RestUtilities from '../../services/RestUtilities';
import MfsRichTextEditor from '../../components/Common/RichText/RichTextEditor';
import { Artist, ArtistPrice } from '../../@types/api';
import dayjs, { Dayjs } from 'dayjs';
import {
  artistCategory,
  artistStatus,
  dbDayFormat,
  dbTimeFormat,
  fullDayFormat,
  pricetypesRequiringHotel,
} from '../../Constants';
import { not } from '../../Functions';
import { useSetRecoilState } from 'recoil';
import { toastState } from '../../state/RecoilState';

const getSteps = () => {
  return [
    'Setup Enquiry',
    'Set Shortlist',
    'Add Alternatives',
    'Prepare Email',
    'Email Confirmation',
  ];
};

const sxProps = {
  root: {
    pt: 2,
    backgroundColor: '#FFF',
  },
  container: {
    pb: 2,
  },
  paper: {
    pl: 2,
    pr: 2,
    mt: 1,
    overflow: 'auto',
  },
  listItem: {
    p: 0,
  },
  button: {
    m: 0.5,
  },
  buttons: {
    mt: 2,
    display: 'block',
  },
  buttonGroup: {
    marginBottom: 3,
  },
  editorWrapper: {
    p: 1,
  },
  spinner: {
    m: '0 auto',
    pt: '160px',
    width: '50px',
  },
};

export default function ProcessEnquiry() {
  const { id } = useParams();
  const setToast = useSetRecoilState(toastState);
  const [activeStep, setActiveStep] = useState<number>(0);
  const [data, setData] = useState<any | null>(null);
  const [allArtists, setAllArtists] = useState<Artist[]>([]);
  const [bookedArtists, setBookedArtists] = useState<Artist[]>([]);
  const [availableArtists, setAvailableArtists] = useState<Artist[]>([]);
  const [alternativeArtists, setAlternativeArtists] = useState<Artist[]>([]);
  const [showcases, setShowcases] = useState<null | any[]>(null);
  const [shortlist, setShortlist] = useState<Artist[]>([]);
  const [eventDate, setEventDate] = useState<Dayjs | null>(null);
  const [startTime, setStartTime] = useState<Dayjs | null>(null);
  const [endTime, setEndTime] = useState<Dayjs | null>(null);
  const [eventType, setEventType] = useState<string>('1');
  const [status, setStatus] = useState<string>('1');
  const [venueName, setVenueName] = useState<string>('');
  const [location, setLocation] = useState<string>('');
  const [priceType, setPriceType] = useState<keyof ArtistPrice>('priceA');
  const [useClientTimes, setUseClientTimes] = useState<boolean>(false);
  const [useAvailableArtists, setUseAvailableArtists] = useState<boolean>(true);
  const [useShowcase, setUseShowcase] = useState<boolean>(true);
  const [emailResult, setEmailResult] = useState<string>('');
  const [editor, setEditor] = useState<any | undefined>(undefined);
  const steps = getSteps();

  useEffect(() => {
    const getAllData = async () => {
      const res = await RestUtilities.get(`/api/enquiry/Get/${id}`);
      var data = res.content;
      setData(data);
      setAllArtists(
        data.artists.filter((a: Artist) => a.status === artistStatus.LIVE)
      );
      setShortlist(data.shortlist);
      setEventDate(dayjs(data.enquiry.eventDate));
      setStartTime(dayjs(data.eventStartTime));
      setEndTime(dayjs(data.eventEndTime));
      setEventType(data.enquiry.eventTypeId);
      setStatus(data.enquiry.statusId);
      setVenueName(data.enquiry.venueName);
      setLocation(data.enquiry.location);
      setPriceType(data.enquiry.priceType as keyof ArtistPrice);
      setToast('Enquiry Loaded');
    };
    if (!data) {
      getAllData();
    }
  }, [data, id, setToast]);

  if (!data) return null;

  const handleNext = async () => {
    if (activeStep === 1) {
      await handleUpdate();
    }
    if (activeStep === 3) {
      await handleSendEmail();
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleSendEmail = async () => {
    const markup = editor?.getHTML();
    const plainText = editor.state.doc.textContent;
    const email = { markup, plainText };
    var res = await RestUtilities.post(`/api/enquiry/SendEmail/${id}`, email);
    if (res.ok) {
      setEmailResult('Your email was sent successfuly');
      setToast('Your email was sent successfuly');
    } else {
      setEmailResult('Something went wrong sending the email');
      setToast('Something went wrong sending the email');
    }
  };

  const handleUpdate = async () => {
    const enquiry = {
      enquiry: {
        enquiryId: id,
        eventTypeId: eventType,
        statusId: status,
        priceType: priceType,
        location: location,
        venueName,
        eventDate: eventDate?.format(dbDayFormat),
      },
      shortlist,
      eventStartTime: startTime?.format(dbTimeFormat),
      eventEndTime: endTime?.format(dbTimeFormat),
    };
    var res = await RestUtilities.post(`/api/enquiry/update/${id}`, enquiry);
    const data = res.content;
    var ids = data.bookedArtistIds;
    var availableArtists = allArtists.filter(
      (x: Artist) => !ids.includes(x.artistId)
    );
    setAvailableArtists(availableArtists);
    setBookedArtists(ids);
    setShowcases(data.nextShowcases);
    setToast('Enquiry Updated');
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const getStepButtonText = () => {
    switch (activeStep) {
      case 0:
        return 'Shortlist >';
      case 1:
        return 'Save and continue >';
      case 2:
        return 'Compose Email >';
      case 3:
        return 'Send Email';
      default:
        return 'Next';
    }
  };

  const getStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <SetUp
            data={data}
            status={status}
            eventType={eventType}
            venueName={venueName}
            location={location}
            priceType={priceType}
            eventDate={eventDate}
            startTime={startTime}
            endTime={endTime}
            setStatus={setStatus}
            setEventType={setEventType}
            setLocation={setLocation}
            setVenueName={setVenueName}
            setPriceType={setPriceType}
            setEventDate={setEventDate}
            setStartTime={setStartTime}
            setEndTime={setEndTime}
          />
        );
      case 1:
        return (
          <AddRemoveArtist
            left={allArtists.filter((a) =>
              a.categories.some(
                (c) => c.categoryId === artistCategory.WEDDINGBAND
              )
            )}
            right={shortlist}
            setRight={setShortlist}
            leftTitle='All Artists'
            rightTitle='Shortlist'
          />
        );
      case 2:
        return (
          <AddRemoveArtist
            left={not(availableArtists, shortlist).filter((a: Artist) =>
              a.categories.some(
                (c) => c.categoryId === artistCategory.WEDDINGBAND
              )
            )}
            right={alternativeArtists}
            setRight={setAlternativeArtists}
            leftTitle='Add Alternative Artists'
            rightTitle='Suggestions'
          />
        );
      case 3:
        return prepareEmail();
      case 4:
        return emailResult;
      default:
        return 'Unknown step';
    }
  };

  const getTimes = () => {
    return useClientTimes ? (
      <span>
        {startTime?.format('h:mma')} - {endTime?.format('h:mma')}
      </span>
    ) : (
      <span>8pm - midnight</span>
    );
  };

  const getPrice = (item: any) => {
    const isBooked = bookedArtists.includes(item.artistId);
    if (isBooked) return '(Unavailable)';
    const availArtist = availableArtists.find(
      (x) => x.artistId === item.artistId
    );
    if (!availArtist?.artistPrice) {
      return `£ not set in DB`;
    }
    const price =
      availArtist.artistPrice[
        priceType as keyof typeof availArtist.artistPrice
      ] + availArtist.artistPrice['bookingFee'];
    return `£${price}`;
  };

  const emailHtml = () => {
    return (
      <>
        <p>Dear {data.enquiry.firstName},</p>
        <p>
          Thank you for your enquiry. I can confirm that the following bands
          availability on {eventDate?.format(fullDayFormat)} at {venueName},{' '}
          {location}.
        </p>
        <p>
          Their fees are based on them playing from {getTimes()} with a break
          usually during the buffet. Music can be played via an ipod through the
          bands system during this time. Extended playing times can be
          negotiated upon request. This quote is inclusive of any travel costs
          and VAT.
        </p>
        {pricetypesRequiringHotel.includes(priceType) && (
          <p>
            Please be advised that for this location, the band would require
            overnight accommodation. This can be supplied by you or
            alternatively an additional £300.00 can be added to the band's fee.
          </p>
        )}
        {shortlist.length > 0 && (
          <div>
            <p>
              <strong>Your Shortlist:</strong>
            </p>
            <ul>
              {shortlist.map((item: Artist) => {
                const price = getPrice(item);
                const isUnavailable = price === '(Unavailable)';
                return (
                  <li key={item.artistId}>
                    {item.artistName}{' '}
                    {isUnavailable ? <strong>{price}</strong> : price}
                  </li>
                );
              })}
            </ul>
          </div>
        )}
        {useAvailableArtists && alternativeArtists.length > 0 && (
          <div>
            <p>
              <strong>Alternative Bands and Artists available: </strong>
            </p>
            <ul>
              {alternativeArtists.map((item: Artist) => (
                <li key={item.artistId}>
                  {item.artistName} {getPrice(item)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {useShowcase && showcases?.length ? (
          <div>
            <p>
              <strong>Upcoming Wedding Band Showcases:</strong>
            </p>
            <ul>
              {showcases.map((item) => (
                <li key={item.eventId}>
                  {dayjs(item.date).format(fullDayFormat)} -{' '}
                  <a
                    href={`https://musicforscotland.co.uk/showcase/${item.city}`}
                    target='_blank'
                    rel='noopener noreferrer'
                  >
                    {item.location}
                  </a>{' '}
                  ({item.time})
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <p>
          If you require any further information or have any specific
          requirements please feel free to contact us.
        </p>
        <p>Kind regards,</p>
        <p>The Music For Scotland Team</p>
      </>
    );
  };

  const prepareEmail = () => {
    const stringContent = ReactDOMServer.renderToStaticMarkup(emailHtml());
    return (
      <Grid item xs={12} md={12} lg={12}>
        <Typography variant='h6'>Adjust your email</Typography>
        <ButtonGroup sx={{ mb: 2 }}>
          <Button onClick={() => setUseClientTimes(!useClientTimes)}>
            Use {useClientTimes ? 'Default' : 'Specific'} Times
          </Button>
          <Button onClick={() => setUseAvailableArtists(!useAvailableArtists)}>
            {useAvailableArtists ? 'Hide' : 'Show'} Alternative Artists
          </Button>
          {showcases?.length && (
            <Button onClick={() => setUseShowcase(!useShowcase)}>
              Toggle Showcase
            </Button>
          )}
        </ButtonGroup>
        <MfsRichTextEditor
          setEditor={(editor) => setEditor(editor)}
          content={stringContent}
          setTouched={() => {}}
          isSatellite={false}
        />
      </Grid>
    );
  };

  return (
    <Box>
      <Typography variant='h5' color='primary' gutterBottom>
        Process Enquiry {data.enquiry.enquiryId}
      </Typography>
      <Stepper activeStep={activeStep}>
        {steps.map((label) => {
          return (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          );
        })}
      </Stepper>
      <Grid container mt={2}>
        {getStepContent()}
        <Grid item sx={sxProps.buttons} xs={12} md={12} lg={12}>
          <Button
            disabled={activeStep === 0}
            variant='contained'
            onClick={handleBack}
            sx={sxProps.button}
          >
            {'< Back'}
          </Button>
          {activeStep < 4 && (
            <Button
              variant='contained'
              color='primary'
              onClick={handleNext}
              sx={sxProps.button}
            >
              {getStepButtonText()}
            </Button>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}
